import { FAILURE_FLAG, INTERCOM_SCRIPT_ID } from './constants';

/**
 * Retrieves the Intercom App ID from environment variables.
 * @returns {string|undefined} - The Intercom App ID or undefined if not set.
 */
export const getIntercomAppId = () => process.env.REACT_APP_INTERCOM;

/**
 * Marks Intercom as failed with an optional reason and error.
 * @param {string} reason - The reason for failure.
 * @param {Error} [error] - The underlying error, if any.
 */
const markFailed = (reason, error) => {
  if (typeof window === 'undefined') return;
  if (!window[FAILURE_FLAG]) {
    window[FAILURE_FLAG] = reason || true;
    console.warn('Intercom disabled.', reason, error);
  }
};

/**
 * Checks if Intercom has previously failed to load.
 * @returns {boolean} - True if Intercom has failed, false otherwise.
 */
export const hasIntercomFailed = () => {
  if (typeof window === 'undefined') return true;
  return Boolean(window[FAILURE_FLAG]);
};

/**
 * Loads the Intercom script asynchronously.
 * If Intercom has previously failed, it will not attempt to load again.
 */
export const loadIntercom = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (hasIntercomFailed()) return;

  const APP_ID = getIntercomAppId();
  if (!APP_ID) return;

  const win = window;
  const doc = document;
  const existing = win.Intercom;

  if (typeof existing === 'function') {
    try {
      existing('reattach_activator');
      existing('update', win.intercomSettings);
    } catch (error) {
      markFailed('intercom-update-failed', error);
    }
    return;
  }

  const intercomQueue = function () {
    intercomQueue.c(arguments);
  };
  intercomQueue.q = [];
  intercomQueue.c = function (args) {
    intercomQueue.q.push(args);
  };
  win.Intercom = intercomQueue;

  const injectScript = () => {
    if (hasIntercomFailed()) return;
    if (doc.getElementById(INTERCOM_SCRIPT_ID)) return;

    try {
      const script = doc.createElement('script');
      script.id = INTERCOM_SCRIPT_ID;
      script.type = 'text/javascript';
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.src = 'https://widget.intercom.io/widget/' + APP_ID;
      script.onerror = () => markFailed('intercom-script-error');
      const anchor = doc.getElementsByTagName('script')[0] || doc.head || doc.body;
      if (anchor?.parentNode) {
        anchor.parentNode.insertBefore(script, anchor);
      } else {
        anchor?.appendChild(script);
      }
    } catch (error) {
      markFailed('intercom-inject-error', error);
    }
  };

  const scheduleInject = () => {
    if (hasIntercomFailed()) return;
    if ('requestIdleCallback' in win) {
      win.requestIdleCallback(injectScript, { timeout: 3000 });
    } else {
      win.setTimeout(injectScript, 1500);
    }
  };

  if (doc.readyState === 'complete') {
    scheduleInject();
  } else {
    const onLoad = () => {
      win.removeEventListener('load', onLoad);
      scheduleInject();
    };
    win.addEventListener('load', onLoad);
  }
};

export { markFailed as markIntercomFailed };
